<script setup lang="ts">
import RPGContainer from "@/components/RPGContainer/RPGContainer.vue";
import RPGAvatar from "@/components/PlayerRPGBars/RPGAvatar.vue";
import {computed} from "vue";

interface Character {
  name?: string;
  title?: string;
  avatar?: string;
  /** Character is Signed with Flow — under game master authority. */
  game_master_mode?: boolean;
}

const props = defineProps<{
  direction?: 'left' | 'right';
  character?: Character;
  /** Rendered above the name as a small eyebrow — "Challenger" / "Defender". */
  role?: string;
}>();

const direction = computed(() => props.direction || 'left');
const character = computed(() => props.character || {
  name: 'The Arbiter',
  title: 'The God of the Arena',
  avatar: undefined,
});
</script>

<template>
  <div class="holder" :class="`holder--${direction}`">
    <RPGAvatar class="character-avatar" :avatar-url="character.avatar" :signed="character.game_master_mode"/>
    <RPGContainer class="character-container">
      <div class="info--holder">
        <p v-if="role" class="character-role">{{ role }}</p>
        <h1 class="character-name">{{ character.name }}</h1>
        <div class="rule" aria-hidden="true"></div>
        <h2 class="character-rank">{{ character.title }}</h2>
      </div>
    </RPGContainer>
  </div>
</template>

<style scoped>
/*
 * Both plates are laid out from one set of rules and mirrored with `flex-direction`,
 * rather than duplicated into left/right template branches. The duplicate branches were
 * what let the two sides drift apart.
 *
 * Every vertical measurement here is fixed rather than content-derived. A fighter whose
 * name wraps to two lines used to make its plate taller than the opposite one, and since
 * the plates are anchored from the bottom, the whole block shifted up on that side only.
 * Reserving two lines of name on both sides keeps the pair on a shared baseline whatever
 * the names are.
 */

.holder {
  /* The avatar overlaps the plate's inner edge, so the text column has to keep clear of
     it on that side only. Both insets are named here and mirrored in one rule below,
     rather than being re-spelled per direction at every breakpoint. */
  --plate-avatar-clear: 5.5rem;
  --plate-edge: 1.6rem;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: min(34rem, 100%);
  margin: 0 auto;
}

.holder--right {
  flex-direction: row-reverse;
}

.character-avatar {
  width: 9rem;
  flex: none;
  overflow: hidden;
  z-index: 2;
  margin-right: -4.5rem;
}

.holder--right .character-avatar {
  margin-right: 0;
  margin-left: -4.5rem;
}

.character-container {
  flex: 1;
  min-width: 0;
  z-index: 1;
}

/*
 * RPGContainer is itself `display: flex`, which makes this column a flex item — so
 * without `flex: 1` it shrank to fit its longest line. That is what put the two plates'
 * text at different offsets: each block was as wide as its own fighter's name happened to
 * be, and centred inside that, so a short name drifted toward the outer edge while a long
 * one filled the plate. Claiming the full column and stretching the children onto it
 * gives both sides one shared measure, and the divider a width that means something.
 */
.info--holder {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 0.3rem;
  text-align: center;
  padding-block: 0.55rem;
  padding-inline: var(--plate-avatar-clear) var(--plate-edge);
}

.holder--right .info--holder {
  padding-inline: var(--plate-edge) var(--plate-avatar-clear);
}

.character-role {
  margin: 0;
  font-family: var(--pl-font-body);
  font-size: var(--pl-text-label);
  font-weight: 600;
  letter-spacing: var(--pl-track-eyebrow);
  text-transform: uppercase;
  color: rgba(126, 220, 232, 0.66);
}

/*
 * --pl-font-display is Orbitron, whose Cyrillic gap is handled by the stack itself:
 * Ukrainian falls through to IBM Plex Sans, which is the documented, deliberate
 * behaviour everywhere else in the app.
 */
.character-name {
  margin: 0;
  font-family: var(--pl-font-display);
  font-weight: 700;
  font-size: clamp(0.92rem, 1.15vw, 1.25rem);
  line-height: 1.28;
  letter-spacing: var(--pl-track-tight);
  text-transform: uppercase;
  text-align: center;
  color: var(--pl-text);
  text-shadow: 0 0 1.1rem rgba(95, 216, 242, 0.22);

  /* Two lines are always reserved, and a third is clamped away. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  min-height: calc(2 * 1.28em);
}

.rule {
  width: 100%;
  height: 1px;
  background: linear-gradient(
      to right,
      rgba(121, 194, 212, 0) 0%,
      rgba(121, 194, 212, 0.5) 25%,
      rgba(121, 194, 212, 0.5) 75%,
      rgba(121, 194, 212, 0) 100%
  );
}

.character-rank {
  margin: 0;
  font-family: var(--pl-font-body);
  font-weight: 500;
  font-size: var(--pl-text-sm);
  line-height: var(--pl-leading-tight);
  letter-spacing: var(--pl-track-label);
  text-transform: uppercase;
  text-align: center;
  color: var(--pl-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

@media (max-width: 900px) {
  .holder {
    --plate-avatar-clear: 4.5rem;
    --plate-edge: 1.2rem;
  }

  .character-avatar {
    width: 7rem;
    margin-right: -3.5rem;
  }

  .holder--right .character-avatar {
    margin-right: 0;
    margin-left: -3.5rem;
  }
}

@media (max-width: 560px) {
  .holder {
    --plate-avatar-clear: 3.4rem;
    --plate-edge: 0.9rem;
  }

  .character-avatar {
    width: 5.25rem;
    margin-right: -2.6rem;
  }

  .holder--right .character-avatar {
    margin-right: 0;
    margin-left: -2.6rem;
  }

  .character-role {
    display: none;
  }
}

</style>
